import { useEffect, useState } from 'react';
import { SwanPathTypeEnum, SwanStyleSheetKey, getSwanStylesheetUrl, styleKeyVarMap, getSwanBaseUrl } from '@vp/swan';
import { useNRLogging } from './useNRLogging';

/**
 * Custom hook to manage Swan stylesheets.
 * Checks for existing Swan stylesheets in the document,
 * loads missing ones, and informs when loading is complete.
 */

export const useSwanCustomStyles = (locale: string, tenant?: string) => {
  const { logCustomAttributesNR } = useNRLogging();
  const [isLoading, setIsLoading] = useState(true);
  const [loadedStyles, setLoadedStyles] = useState<SwanStyleSheetKey[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const requiredStyles: SwanStyleSheetKey[] = ['core', 'alertBox', 'button', 'fileTile', 'icon', 'selectionSet', 'spinner', 'statusMessage'];

  useEffect(() => {
    const loadSwanStyles = async () => {
      try {
        // get all styles for the loaded document
        const style = getComputedStyle(document.documentElement);
        // determine which styles are missing from the required styles
        const stylesToLoad = requiredStyles.filter((key) => {
          const cssVar = styleKeyVarMap[key];

          // Some keys don't have a corresponding CSS variables
          if (!cssVar) return false;
          return style.getPropertyValue(cssVar) !== '1';
        });

        if (stylesToLoad.length === 0) {
          setIsLoading(false);
          return;
        }

        // Load missing styles
        const stylePromises = stylesToLoad.map((style) => {
          return new Promise<SwanStyleSheetKey>((resolve, reject) => {
            const swanBaseURL = getSwanBaseUrl(tenant, locale);
            console.info(`ca-chatbot: Swan base URL for tenant ${tenant}, locale ${locale} is ${swanBaseURL}`);
            const url = getSwanStylesheetUrl(style, SwanPathTypeEnum.hashed, swanBaseURL);
            if (url) {
              console.info(`ca-chatbot: loading Swan stylesheet for ${style}`);
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.type = 'text/css';
              link.href = url;
              link.onload = () => resolve(style);
              link.onerror = () => reject(new Error(`Failed to load styles for ${style}`));
              document.head.appendChild(link);
            }
          });
        });

        const loaded = await Promise.all(stylePromises);
        setLoadedStyles(loaded);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error loading styles'));
        logCustomAttributesNR('ca-chatbot-init-error', { logLevel: 'error', message: err.message, errorType: 'stylesError' });
        setIsLoading(false);
      }
    };

    loadSwanStyles();
  }, [locale, tenant]);

  return { isLoading, loadedStyles, error };
};

export default useSwanCustomStyles;
